import { useCallback,useEffect,useMemo,useState } from "react";
import {
  Box,Typography,Button,CircularProgress,Checkbox,FormControl,InputLabel,MenuItem,Select,
  Switch,Table,TableBody,TableCell,TableContainer,TableHead,TableRow
} from "@mui/material";
import { useNavigate,useParams } from "react-router-dom";
import { useAppTheme } from "../../../context/ThemeContext";
import { useAlert } from "../../../context/AlertContext";

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find((row)=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

export default function TournamentConfiguration(){
  const {slug}=useParams();
  const navigate=useNavigate();
  const {colors,typography}=useAppTheme();
  const {showSuccess,showError}=useAlert();

  const [tournament,setTournament]=useState(null);
  const [options,setOptions]=useState([]);
  const [categories,setCategories]=useState([]);
  const [selectedCategory,setSelectedCategory]=useState("");
  const [loading,setLoading]=useState(true);
  const [addingCategory,setAddingCategory]=useState(false);
  const [savingCategory,setSavingCategory]=useState("");
  const [changingCategory,setChangingCategory]=useState("");

  const loadData=useCallback(async()=>{
    try{
      setLoading(true);

      const [optionsResponse,categoriesResponse]=await Promise.all([
        fetch(`/tournaments/${slug}/options/`,{credentials:"include"}),
        fetch(`/tournaments/${slug}/categories/`,{credentials:"include"})
      ]);

      const optionsData=await readResponse(optionsResponse);
      const categoriesData=await readResponse(categoriesResponse);

      if(!optionsResponse.ok){
        throw new Error(optionsData.error||optionsData.detail||"No se pudo cargar la configuración del torneo.");
      }

      if(!categoriesResponse.ok){
        throw new Error(categoriesData.error||categoriesData.detail||"No se pudieron cargar las categorías del torneo.");
      }

      setTournament(optionsData.tournament||null);
      setOptions(Array.isArray(optionsData.categories)?optionsData.categories:[]);
      setCategories(Array.isArray(categoriesData)?categoriesData:[]);
    }catch(error){
      showError(error.message||"No se pudo cargar la configuración del torneo.");
    }finally{
      setLoading(false);
    }
  },[slug,showError]);

  useEffect(()=>{
    loadData();
  },[loadData]);

  const availableCategories=useMemo(()=>{
    const registered=new Set(
      categories.filter((item)=>item.active).map((item)=>item.category.slug)
    );

    return options.filter((item)=>!registered.has(item.slug));
  },[options,categories]);

  const handleAddCategory=async()=>{
    if(!selectedCategory){
      showError("Selecciona una categoría.");
      return;
    }

    try{
      setAddingCategory(true);

      const response=await fetch(`/tournaments/${slug}/categories/`,{
        method:"POST",
        credentials:"include",
        headers:{
          "Content-Type":"application/json",
          "X-CSRFToken":getCookie("csrftoken")
        },
        body:JSON.stringify({category:selectedCategory})
      });

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||data.detail||"No se pudo agregar la categoría.");
      }

      setSelectedCategory("");
      await loadData();
      showSuccess("Categoría agregada correctamente.");
    }catch(error){
      showError(error.message||"No se pudo agregar la categoría.");
    }finally{
      setAddingCategory(false);
    }
  };

  const handleCategoryStatus=async(item)=>{
    if(changingCategory) return;

    try{
      setChangingCategory(item.category.slug);

      const response=await fetch(
        `/tournaments/${slug}/categories/${item.category.slug}/`,
        {
          method:"PATCH",
          credentials:"include",
          headers:{
            "Content-Type":"application/json",
            "X-CSRFToken":getCookie("csrftoken")
          },
          body:JSON.stringify({active:!item.active})
        }
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||data.detail||"No se pudo actualizar la categoría.");
      }

      await loadData();
      showSuccess(data.active?"Categoría activada correctamente.":"Categoría desactivada correctamente.");
    }catch(error){
      showError(error.message||"No se pudo actualizar la categoría.");
    }finally{
      setChangingCategory("");
    }
  };

  const toggleTeam=(categorySlug,teamSlug)=>{
    setCategories((current)=>
      current.map((item)=>{
        if(item.category.slug!==categorySlug) return item;

        const existing=item.teams.find((registration)=>registration.team.slug===teamSlug);

        if(existing){
          return {
            ...item,
            teams:item.teams.map((registration)=>
              registration.team.slug===teamSlug?
                {...registration,active:!registration.active}:
                registration
            )
          };
        }

        const categoryOption=options.find((option)=>option.slug===categorySlug);
        const team=categoryOption?.teams?.find((optionTeam)=>optionTeam.slug===teamSlug);

        if(!team) return item;

        return {
          ...item,
          teams:[...item.teams,{team,active:true}]
        };
      })
    );
  };

  const selectAllTeams=(categorySlug)=>{
    const categoryOption=options.find((option)=>option.slug===categorySlug);
    if(!categoryOption) return;

    setCategories((current)=>
      current.map((item)=>{
        if(item.category.slug!==categorySlug) return item;

        const activeSlugs=new Set(
          item.teams.filter((registration)=>registration.active).map((registration)=>registration.team.slug)
        );

        const allSelected=
          categoryOption.teams.length>0&&
          categoryOption.teams.every((team)=>activeSlugs.has(team.slug));

        if(allSelected){
          return {
            ...item,
            teams:item.teams.map((registration)=>({...registration,active:false}))
          };
        }

        return {
          ...item,
          teams:categoryOption.teams.map((team)=>{
            const existing=item.teams.find((registration)=>registration.team.slug===team.slug);
            return existing?{...existing,active:true}:{team,active:true};
          })
        };
      })
    );
  };

  const handleSaveTeams=async(item)=>{
    if(savingCategory) return;

    const selectedTeams=item.teams
      .filter((registration)=>registration.active)
      .map((registration)=>registration.team.slug);

    try{
      setSavingCategory(item.category.slug);

      const response=await fetch(
        `/tournaments/${slug}/categories/${item.category.slug}/teams/`,
        {
          method:"POST",
          credentials:"include",
          headers:{
            "Content-Type":"application/json",
            "X-CSRFToken":getCookie("csrftoken")
          },
          body:JSON.stringify({teams:selectedTeams})
        }
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||data.detail||"No se pudieron guardar los equipos.");
      }

      await loadData();
      showSuccess("Equipos actualizados correctamente.");
    }catch(error){
      showError(error.message||"No se pudieron guardar los equipos.");
    }finally{
      setSavingCategory("");
    }
  };

  if(loading){
    return (
      <Box sx={{minHeight:300,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <CircularProgress size={28} sx={{color:colors.secondary}} />
      </Box>
    );
  }

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box sx={{mb:2}}>
        <Typography sx={{fontSize:{xs:typography.sizes.xl,md:typography.sizes.title},fontWeight:700,color:colors.text}}>
          {tournament?.name||"Configuración del torneo"}
        </Typography>

        <Typography sx={{mt:0.4,fontSize:typography.sizes.md,color:colors.textSecondary}}>
          Configura las categorías y los equipos que participarán en el torneo.
        </Typography>
      </Box>

      <Box sx={{mb:2}}>
        <Typography sx={{mb:0.4,fontSize:typography.sizes.lg,fontWeight:700,color:colors.text}}>
          Categorías
        </Typography>

        <Typography sx={{mb:1.2,fontSize:typography.sizes.md,color:colors.textSecondary}}>
          Agrega las categorías que se disputarán en este torneo.
        </Typography>

        <Box sx={{display:"flex",flexDirection:{xs:"column",sm:"row"},alignItems:{xs:"stretch",sm:"center"},gap:1}}>
          <FormControl size="small" sx={{width:{xs:"100%",sm:320}}}>
            <InputLabel>Categoría</InputLabel>

            <Select value={selectedCategory} label="Categoría" onChange={(event)=>setSelectedCategory(event.target.value)}>
              {availableCategories.map((category)=>(
                <MenuItem key={category.slug} value={category.slug}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            size="small"
            variant="contained"
            disabled={!selectedCategory||addingCategory}
            onClick={handleAddCategory}
            sx={{px:2,textTransform:"none",backgroundColor:colors.secondary,"&:hover":{backgroundColor:colors.secondaryHover}}}
          >
            {addingCategory?"Agregando...":"Agregar categoría"}
          </Button>
        </Box>
      </Box>

      {categories.length===0?(
        <TableContainer sx={{border:`1px solid ${colors.border}`,borderRadius:"10px",backgroundColor:colors.surface,boxShadow:"none"}}>
          <Table>
            <TableHead>
              <TableRow sx={{backgroundColor:colors.tableHeader}}>
                <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>Categorías del torneo</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow>
                <TableCell align="center" sx={{py:4,color:colors.textSecondary}}>
                  Este torneo todavía no tiene categorías configuradas.
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ):(
        <Box sx={{display:"flex",flexDirection:"column",gap:2}}>
          {categories.map((item)=>{
            const categoryOption=options.find((option)=>option.slug===item.category.slug);
            const availableTeams=categoryOption?.teams||[];

            const selectedSlugs=new Set(
              item.teams.filter((registration)=>registration.active).map((registration)=>registration.team.slug)
            );

            const allSelected=
              availableTeams.length>0&&
              availableTeams.every((team)=>selectedSlugs.has(team.slug));

            return (
              <Box key={item.category.slug}>
                <TableContainer sx={{border:`1px solid ${colors.border}`,borderRadius:"10px",backgroundColor:colors.surface,boxShadow:"none",overflow:"hidden"}}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{backgroundColor:colors.tableHeader}}>
                        <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>Categoría</TableCell>
                        <TableCell align="center" sx={{color:colors.tableHeaderText,fontWeight:700}}>Equipos</TableCell>
                        <TableCell align="center" sx={{color:colors.tableHeaderText,fontWeight:700}}>Activa</TableCell>
                        <TableCell align="right" sx={{color:colors.tableHeaderText,fontWeight:700}}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow>
                        <TableCell sx={{fontWeight:600,color:colors.text}}>
                          {item.category.name}
                        </TableCell>

                        <TableCell align="center" sx={{color:colors.text}}>
                          {selectedSlugs.size}
                        </TableCell>

                        <TableCell align="center">
                          <Switch
                            size="small"
                            checked={item.active}
                            disabled={changingCategory===item.category.slug}
                            onChange={()=>handleCategoryStatus(item)}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <Button
                            size="small"
                            disabled={!item.active||selectedSlugs.size===0}
                            onClick={()=>navigate(`/admin/tournaments/${slug}/${item.category.slug}/phases`)}
                            sx={{minWidth:0,textTransform:"none",color:colors.secondary}}
                          >
                            Fases
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {item.active&&(
                  <Box sx={{mt:1.2}}>
                    <Box sx={{mb:1,display:"flex",alignItems:{xs:"flex-start",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:0.8}}>
                      <Box>
                        <Typography sx={{fontSize:typography.sizes.lg,fontWeight:700,color:colors.text}}>
                          Equipos participantes
                        </Typography>

                        <Typography sx={{mt:0.2,fontSize:typography.sizes.sm,color:colors.textSecondary}}>
                          Selecciona los equipos inscritos en {item.category.name}.
                        </Typography>
                      </Box>

                      {availableTeams.length>0&&(
                        <Button
                          size="small"
                          onClick={()=>selectAllTeams(item.category.slug)}
                          sx={{textTransform:"none",color:colors.secondary}}
                        >
                          {allSelected?"Quitar todos":"Seleccionar todos"}
                        </Button>
                      )}
                    </Box>

                    <TableContainer sx={{border:`1px solid ${colors.border}`,borderRadius:"10px",backgroundColor:colors.surface,boxShadow:"none",overflow:"hidden"}}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{backgroundColor:colors.tableHeader}}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={allSelected}
                                indeterminate={selectedSlugs.size>0&&!allSelected}
                                disabled={availableTeams.length===0}
                                onChange={()=>selectAllTeams(item.category.slug)}
                                sx={{
                                  color:colors.tableHeaderText,
                                  "&.Mui-checked":{color:colors.tableHeaderText},
                                  "&.MuiCheckbox-indeterminate":{color:colors.tableHeaderText}
                                }}
                              />
                            </TableCell>

                            <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>Equipo</TableCell>
                            <TableCell align="center" sx={{color:colors.tableHeaderText,fontWeight:700}}>Participa</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {availableTeams.length===0?(
                            <TableRow>
                              <TableCell colSpan={3} align="center" sx={{py:4,color:colors.textSecondary}}>
                                No existen equipos activos inscritos en esta categoría.
                              </TableCell>
                            </TableRow>
                          ):(
                            availableTeams.map((team)=>{
                              const selected=selectedSlugs.has(team.slug);

                              return (
                                <TableRow key={team.slug} hover onClick={()=>toggleTeam(item.category.slug,team.slug)} sx={{cursor:"pointer","&:last-child td":{borderBottom:0}}}>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      size="small"
                                      checked={selected}
                                      onChange={()=>toggleTeam(item.category.slug,team.slug)}
                                      onClick={(event)=>event.stopPropagation()}
                                    />
                                  </TableCell>

                                  <TableCell>
                                    <Box sx={{display:"flex",alignItems:"center",gap:1.2}}>
                                      {team.logo&&(
                                        <Box component="img" src={team.logo} alt={team.name} sx={{width:34,height:34,objectFit:"contain",flexShrink:0}} />
                                      )}

                                      <Typography sx={{fontSize:typography.sizes.md,fontWeight:600,color:colors.text}}>
                                        {team.name}
                                      </Typography>
                                    </Box>
                                  </TableCell>

                                  <TableCell align="center">
                                    <Switch
                                      size="small"
                                      checked={selected}
                                      onChange={()=>toggleTeam(item.category.slug,team.slug)}
                                      onClick={(event)=>event.stopPropagation()}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Box sx={{mt:1,display:"flex",justifyContent:"flex-end"}}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={savingCategory===item.category.slug}
                        onClick={()=>handleSaveTeams(item)}
                        sx={{px:2,textTransform:"none",backgroundColor:colors.secondary,"&:hover":{backgroundColor:colors.secondaryHover}}}
                      >
                        {savingCategory===item.category.slug?"Guardando...":"Guardar equipos"}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}